import React, { useRef } from "react";
import * as filesService from "../../services/filesService";
import "../../sass/zeplin/FileUpload.scss";
import { makeToast } from "helpers/common";

const defaultGetErrorMessage = (err) => {
  if (!err) return "Upload failed. Please try again.";
  if (typeof err === "string") return err;
  if (err?.message && typeof err.message === "string") return err.message;

  // axios/fetch style shapes sometimes put useful info here
  const maybeText =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data ||
    err?.error;

  if (typeof maybeText === "string") return maybeText;

  try {
    return JSON.stringify(err);
  } catch {
    return "Upload failed. Please try again.";
  }
};

const FileUpload = (props) => {
  const {
    onSuccess = null,
    onError = () => {},
    onStartUpload = null,

    // NEW (optional)
    onUploadingChange = null,
    maxSizeBytes = 2000000, // default 2MB
    disabled = false,
    accept,
    multiple = false,
  } = props;

  const inputRef = useRef(null);

  const handleChange = async (e) => {
    const fileList = e.target.files;

    // allow selecting the same file again later
    const resetInput = () => {
      if (inputRef.current) inputRef.current.value = "";
    };

    if (disabled) {
      resetInput();
      return;
    }

    if (!fileList || fileList.length === 0) {
      resetInput();
      return;
    }

    // validate sizes (mobile-friendly: toast, not alert)
    for (const f of fileList) {
      if (typeof f?.size === "number" && f.size > maxSizeBytes) {
        const mb = (maxSizeBytes / (1024 * 1024)).toFixed(0);
        onError(`File "${f.name}" is too large. Max size is ${mb}MB.`);
        resetInput();
        return;
      }
    }

    const formData = new FormData();

    try {
      for (let file of fileList) {
        formData.append("files", file);
      }

      if (onUploadingChange && typeof onUploadingChange === "function")
        onUploadingChange(true);

      if (onStartUpload && typeof onStartUpload === "function") onStartUpload();
      else makeToast("Uploading", "info", 1000);

      const res = await filesService.uploadFile(formData);

      if (onSuccess && typeof onSuccess === "function") onSuccess(res);
      else makeToast("Upload Success", "success", 1000);
    } catch (err) {
      const msg = defaultGetErrorMessage(err);
      onError(msg);
    } finally {
      if (onUploadingChange && typeof onUploadingChange === "function")
        onUploadingChange(false);

      resetInput();
    }
  };

  return (
    <label
      className={`FileUpload ${props.labelClassName}`}
      style={{
        ...props.style,
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
      aria-disabled={disabled ? "true" : "false"}
    >
      {props.children}
      <input
        ref={inputRef}
        type="file"
        name="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        className={`FileUpload ${props.className}`}
        onChange={handleChange}
      />
    </label>
  );
};

export default FileUpload;
